import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { ProgressBar } from 'react95';
import { isEmpty } from 'lodash-es';
import history from '../../keeperHistory';
import QDeckToolBar from '../Common/QDeckToolBar';
import ErrorModal from '../Common/ErrorModal';
import TaskDetailHeader from './TaskDetailHeader';
import TaxpayerInfoTable from './TaxpayerInfoTable';
import SubstagesTable from './SubstagesTable';
import OpsInfoTable from './OpsInfoTable';
import TaskActivity from './TaskActivity';
import UserEscModal from './UserEscModal';
import ConfirmationModal from './ConfirmationModal';
import {
  opsInfoVisibleSelector,
  taskDetailErrorMsgSelector,
  tpInfoVisibleSelector,
  substagesVisibleSelector,
  taskDetailResTitleSelector,
  taskDetailResFooterSelector,
  modalTypeSelector
} from '../../selectors/taskDetailSelectors';
import { subscribeTaskDetailSocket } from '../../actions/socketActions';
import {
  setTpInfoVisible,
  setSubstagesVisible,
  setOpsInfoVisible,
  setTaskDetailResMsg
} from '../../actions/taskDetailActions';
import { ACTION_STAGE_MAP, TASK_DETAIL_ACTIONS, TASK_SUBSTAGES, WEBOPS_URL } from '../../global/Environment';
import './TaskDetail.scss';
import { TAGS } from '../../api/baseApi';
import { opsSystemApi, useGetTaskQuery } from '../../api/opsSystemApi';

const TaskDetail = ({
  tpInfoVisible,
  substagesVisible,
  opsInfoVisible,
  errorMsg,
  resTitle,
  resFooter,
  modalType,
  subscribeTaskDetailSocket,
  setTpInfoVisible,
  setSubstagesVisible,
  setOpsInfoVisible,
  setTaskDetailResMsg
}) => {
  const { taskId } = useParams();
  const [loadingProgress, setLoadingProgress] = useState(50);

  const dispatch = useDispatch();

  const { data: task = {}, isLoading: isTaskLoading } = useGetTaskQuery(taskId);

  const onTaskListClick = useCallback(() => history.push('/task-list'), []);

  const onRefresh = async () => {
    dispatch(
      opsSystemApi.util.invalidateTags([
        TAGS.TASK,
        TAGS.TASK_COMMENTS,
        TAGS.TASK_ACTIVITIES,
        TAGS.TASK_FORMS,
        TAGS.ASK_LOG,
        TAGS.USER_ESCALATION_ERRORS
      ])
    );
  };

  const taskListBtn = {
    name: 'Task List',
    onClick: onTaskListClick
  };
  const selectYearBtn = {
    name: 'Select Year'
  };
  const refreshBtn = {
    name: 'Refresh',
    onClick: onRefresh
  };

  const goToWebOpsBtn = {
    name: 'Go to WebOps',
    onClick: () => window.open(`${WEBOPS_URL}/dashboard?userId=${task.user.id}`, '_blank')
  };

  useEffect(() => {
    // set websocket connection
    subscribeTaskDetailSocket(taskId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEmpty(task)) {
      const timer = setInterval(() => {
        setLoadingProgress((prevPercentage) => {
          if (prevPercentage >= 90) {
            return 90;
          }
          const diff = Math.random() * 10;
          return Math.min(prevPercentage + diff, 90);
        });
      }, 250);

      return () => clearInterval(timer);
    }
  }, [task]);

  const onTaxpayerTabClick = useCallback(() => setTpInfoVisible(true), [setTpInfoVisible]);
  const onSubstageTabClick = useCallback(() => setSubstagesVisible(true), [setSubstagesVisible]);
  const onOpsTabClick = useCallback(() => setOpsInfoVisible(true), [setOpsInfoVisible]);
  const onErrorModalClose = useCallback(() => setTaskDetailResMsg({}), [setTaskDetailResMsg]);

  if (isTaskLoading) {
    return (
      <div className='task-detail'>
        <QDeckToolBar type='task detail' menuItems={[taskListBtn, selectYearBtn, refreshBtn, goToWebOpsBtn]} />
        <div className='task-detail-content'>
          <ProgressBar variant='tile' value={Math.floor(loadingProgress)} />
        </div>
        {errorMsg ? (
          <ErrorModal errorMsg={errorMsg} titleMsg={resTitle} footerMsg={resFooter} onClose={onErrorModalClose} />
        ) : null}
      </div>
    );
  }

  const taxpayerTab = {
    name: 'Taxpayer Info',
    onClick: onTaxpayerTabClick
  };
  const substageTab = {
    name: 'Substages',
    onClick: onSubstageTabClick
  };
  const opsTab = {
    name: 'Ops Details',
    onClick: onOpsTabClick
  };
  const hiddenWindows = [
    !tpInfoVisible && taxpayerTab,
    !substagesVisible && substageTab,
    !opsInfoVisible && opsTab
  ].filter(Boolean);

  return (
    <div className='task-detail'>
      <QDeckToolBar
        type='task detail'
        menuItems={[taskListBtn, selectYearBtn, refreshBtn, goToWebOpsBtn]}
        tabs={isEmpty(hiddenWindows) ? null : hiddenWindows}
      />

      <div className='task-detail-content'>
        <div className='task-detail-content-header'>
          <TaskDetailHeader />
        </div>
        <div className='task-detail-content-body'>
          <div className='task-detail-content-body-left'>
            <TaxpayerInfoTable />
            <SubstagesTable />
            <OpsInfoTable />
          </div>

          <div className='task-detail-content-body-right'>
            <TaskActivity />
          </div>
        </div>
      </div>

      {modalType === TASK_DETAIL_ACTIONS.USER_ESCALATION ? (
        <UserEscModal task={task} modalType={modalType} />
      ) : modalType &&
        (ACTION_STAGE_MAP[modalType] ||
          [TASK_SUBSTAGES.BLOCKED, TASK_SUBSTAGES.EMAIL_OUTBOX, TASK_SUBSTAGES.PAST_YEAR_RETURN].includes(
            modalType
          )) ? (
        <ConfirmationModal modalType={modalType} />
      ) : null}
      {errorMsg ? (
        <ErrorModal errorMsg={errorMsg} titleMsg={resTitle} footerMsg={resFooter} onClose={onErrorModalClose} />
      ) : null}
    </div>
  );
};

const mapStateToProps = (state) => ({
  tpInfoVisible: tpInfoVisibleSelector(state),
  substagesVisible: substagesVisibleSelector(state),
  opsInfoVisible: opsInfoVisibleSelector(state),
  errorMsg: taskDetailErrorMsgSelector(state),
  resTitle: taskDetailResTitleSelector(state),
  resFooter: taskDetailResFooterSelector(state),
  modalType: modalTypeSelector(state)
});

const mapDispatchToProps = {
  subscribeTaskDetailSocket,
  setTpInfoVisible,
  setSubstagesVisible,
  setOpsInfoVisible,
  setTaskDetailResMsg
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskDetail);
